// Google Ads (gtag.js) bridge. The tag itself is loaded statically in
// index.html / shopify.html; this module only pushes events and never throws.
//
// Conversion actions in Google Ads need a per-action label
// (AW-18484826983/<label>). Labels are build-time config, not code:
//   VITE_GADS_CONVERSION_LABELS='{"sign_up":"AbCd...","purchase":"EfGh..."}'
// Events are ALSO sent under their standard names (sign_up, purchase, …) so
// they show up before labels exist and can be imported later.

export const GADS_TAG_ID = 'AW-18484826983';

const FUNNEL_TO_GTAG = {
  signup_completed: 'sign_up',
  trial_activated: 'subscribe',
  preview_subscribe_clicked: 'begin_checkout',
  change_plan_click: 'begin_checkout',
  renew_checkout_clicked: 'begin_checkout',
  social_connected: 'social_connected',
  onboarding_skipped: 'onboarding_skipped',
  quick_brand_created: 'brand_created',
  first_post_created: 'first_post_created',
};

export function mapFunnelEventToGtag(name) {
  return FUNNEL_TO_GTAG[name] || null;
}

// Labels of the conversion actions created in Google Ads (owner, 2026-09-30).
// Build-time env can add/override: VITE_GADS_CONVERSION_LABELS='{"sign_up":"…"}'.
export const DEFAULT_CONVERSION_LABELS = {
  sign_up: 'lkkICJWh54sdEOemoO5E',   // "Sign-up"
  begin_checkout: 'Vf_8CJuh54sdEOemoO5E', // "Begin checkout"
  subscribe: '1NEfCJih54sdEOemoO5E', // "Subscribe" — subscription/trial activated
  purchase: 'CbnUCJKh54sdEOemoO5E',  // "Purchase" — paid checkout returned
};

function conversionLabels() {
  try {
    const raw = import.meta.env.VITE_GADS_CONVERSION_LABELS;
    return { ...DEFAULT_CONVERSION_LABELS, ...(raw ? JSON.parse(raw) : {}) };
  } catch {
    return DEFAULT_CONVERSION_LABELS;
  }
}

export function gtagEvent(name, params, deps = {}) {
  try {
    const g = deps.gtag ?? (typeof window !== 'undefined' ? window.gtag : undefined);
    if (typeof g !== 'function' || !name) return false;
    g('event', name, params && typeof params === 'object' ? params : {});
    const label = (deps.labels ?? conversionLabels())[name];
    if (label) {
      g('event', 'conversion', { send_to: `${GADS_TAG_ID}/${label}`, ...(params || {}) });
    }
    return true;
  } catch {
    return false;
  }
}

// Money conversions are reported once per transaction id even though the
// billing tab re-renders and the URL is only stripped after the first paint.
// transaction_id is mandatory (Google Ads dedupes recurring payments without it).
function trackMoneyOnce(eventName, transactionId, params, deps) {
  const id = String(transactionId || 'checkout').slice(0, 128);
  const key = `dathent.gads.${eventName}.${id}`;
  try {
    const store = deps.storage ?? (typeof window !== 'undefined' ? window.sessionStorage : null);
    if (store && store.getItem(key)) return false;
    const sent = gtagEvent(eventName, { ...(params || {}), transaction_id: id }, deps);
    if (sent && store) store.setItem(key, '1');
    return sent;
  } catch {
    return false;
  }
}

// OpenAI ads pixel (base script is loaded via Google Tag Manager, owner
// 2026-10-02). Its conversion for a paid subscription is a fixed call.
export function openAiPixelSubscription(deps = {}) {
  try {
    const q = deps.oaiq ?? (typeof window !== 'undefined' ? window.oaiq : undefined);
    if (typeof q !== 'function') return false;
    q('measure', 'subscription_created', { type: 'plan_enrollment' });
    return true;
  } catch {
    return false;
  }
}

/** Paid subscription (Stripe Checkout / renewal) → "Subscribe" conversion, value from the invoice. */
export function trackSubscribeOnce(transactionId, params = {}, deps = {}) {
  const sent = trackMoneyOnce('subscribe', transactionId, params, deps);
  if (sent) openAiPixelSubscription(deps);
  return sent;
}

/** One-off payment (e.g. credit top-up) → "Purchase" conversion. */
export function trackPurchaseOnce(transactionId, params = {}, deps = {}) {
  return trackMoneyOnce('purchase', transactionId, params, deps);
}

/** {amountPaid (cents), currency} of the newest paid invoice, or null. */
export function pickInvoiceValue(invoices) {
  try {
    const paid = (Array.isArray(invoices) ? invoices : [])
      .filter((inv) => inv && (inv.status === 'paid' || (inv.amountPaid ?? 0) > 0) && (inv.amountPaid ?? 0) > 0)
      .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    const inv = paid[0];
    if (!inv) return null;
    return { value: Math.round(inv.amountPaid) / 100, currency: String(inv.currency || 'EUR').toUpperCase(), invoiceId: inv.id || null };
  } catch {
    return null;
  }
}
